/**
 * Format tampilan untuk pengguna lapangan.
 *
 * Aturan utama modul ini: **angka yang jujur mengalahkan ikon yang menenangkan.** Centang hijau
 * memberi tahu pengguna bahwa semuanya beres; "Akurasi ±480 m" memberi tahu ia harus pindah ke
 * tempat terbuka. Hanya yang kedua dapat ditindaklanjuti (UI-PAGE-MAP §2).
 */

/** Akurasi GPS dalam angka. `null` = belum terbaca, dan itu dikatakan apa adanya. */
export function formatAccuracy(accuracyMeters: number | null): string {
  if (accuracyMeters === null) {
    return 'Akurasi belum terbaca';
  }
  if (accuracyMeters < 1) {
    return 'Akurasi ±1 m';
  }
  return `Akurasi ±${Math.round(accuracyMeters)} m`;
}

/** Jarak ke lokasi kerja. Di bawah 1 km tetap dalam meter — lebih berguna saat berjalan kaki. */
export function formatDistance(distanceMeters: number | null): string {
  if (distanceMeters === null) {
    return 'Jarak tidak diketahui';
  }
  if (distanceMeters < 1000) {
    return `${Math.round(distanceMeters)} m`;
  }
  return `${(distanceMeters / 1000).toFixed(1)} km`;
}

const VERIFICATION_LABEL: Readonly<Record<string, string>> = {
  VERIFIED: 'Lokasi terverifikasi',
  UNVERIFIED_NO_GPS: 'Tanpa verifikasi lokasi',
  UNVERIFIED_LOW_ACCURACY: 'Akurasi lokasi kurang',
  OUTSIDE_GEOFENCE: 'Di luar radius lokasi kerja',
};

/** Label hasil verifikasi SERVER. Klien hanya menerjemahkan, tidak pernah menyimpulkan sendiri. */
export function formatVerification(verification: string): string {
  return VERIFICATION_LABEL[verification] ?? verification;
}

/** Benar bila status verifikasi layak ditonjolkan — bukan disembunyikan di balik detail. */
export function isUnverified(verification: string): boolean {
  return verification !== 'VERIFIED';
}

const WORK_MODE_LABEL: Readonly<Record<string, string>> = {
  WFO: 'Di kantor',
  WFH: 'Dari rumah',
  FIELD_WORK: 'Kerja lapangan',
  BUSINESS_TRIP: 'Perjalanan dinas',
};

export function formatWorkMode(workMode: string): string {
  return WORK_MODE_LABEL[workMode] ?? workMode;
}

const DAY_STATUS_LABEL: Readonly<Record<string, string>> = {
  PRESENT: 'Hadir',
  LATE: 'Terlambat',
  INCOMPLETE: 'Belum lengkap',
  ON_LEAVE: 'Cuti',
};

export function formatDayStatus(status: string | null): string {
  if (status === null) return 'Belum ada catatan';
  return DAY_STATUS_LABEL[status] ?? status;
}

/**
 * Jam pada timezone organisasi — BUKAN timezone browser.
 *
 * Kurir yang ponselnya masih di zona lain, atau laptop manajer yang diset UTC, tidak boleh
 * mengubah arti "jam berapa ia check-in". Timezone selalu datang dari kebijakan organisasi.
 */
export function formatTimeInZone(iso: string, timeZone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat('id-ID', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

/** Tanggal panjang pada timezone organisasi, mis. "Sabtu, 8 Agustus 2026". */
export function formatDateInZone(iso: string, timeZone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat('id-ID', {
    timeZone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/**
 * Tanggal POLOS (`YYYY-MM-DD`, tanpa jam), mis. "15 September 2026" — untuk `scheduledDate`,
 * bukan timestamp seperti `occurredAtServer`.
 *
 * `timeZone: 'UTC'` DIPAKSA, bukan zona organisasi. `new Date('2026-09-15')` selalu ditafsirkan
 * sebagai tengah malam UTC; memformatnya di zona LAIN (mis. Jakarta, UTC+7) akan menggesernya ke
 * jam 07:00 pada tanggal yang sama — aman untuk Jakarta, tapi bukan aturan yang tahan dipakai di
 * organisasi mana pun. Memformat balik di UTC yang sama dengan yang dipakai mem-parsing-nya
 * membuat tanggal ini tidak pernah bergantung pada zona waktu pembaca sama sekali (pelajaran yang
 * sama dengan `hariDalamPekan` di `rekap-bulanan.ts`).
 */
export function formatPlainDate(dateOnly: string): string {
  const date = new Date(dateOnly);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat('id-ID', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/** Menit terlambat menjadi kalimat, bukan angka telanjang. */
export function formatLateMinutes(lateMinutes: number): string {
  if (lateMinutes <= 0) return 'Tepat waktu';
  if (lateMinutes < 60) return `Terlambat ${lateMinutes} menit`;
  const hours = Math.floor(lateMinutes / 60);
  const minutes = lateMinutes % 60;
  return minutes === 0 ? `Terlambat ${hours} jam` : `Terlambat ${hours} jam ${minutes} menit`;
}
